import { useEffect, useRef } from 'react'

import { setOptimizelyExperiment } from 'actions/OptimizelyActions'
import { optimizelyIsReady, userContext } from 'analytics/optimizely/optimizely'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import createSelector from 'lib/web/createSelector'
import { OptimizelyDecideOption } from '@optimizely/optimizely-sdk'
import { reportClientError } from 'services/errorReportingService'
import config from 'constants/config'

/**
 * This is specifically for a CRO AB test, do not add anything else here.
 * Ticket to clean up: https://aussiecommerce.atlassian.net/browse/CRO-3601
 *
 * Activates the specific hero finder AB test based on the offer id.
 * Should not be used for any other AB tests.
 * @param offerId
 */
export function activateHeroFinderAbTest(offerId: string) {
  const enumKey = isOfferInHeroFinderExperiment(offerId)
  if (enumKey) activateOptimizelyExperiment(OptimizelyServerSideExperiments[enumKey])
}

export function isOfferInHeroFinderExperiment(offerId: string) {
  return Object.keys(OptimizelyServerSideExperiments).find((key) => {
    return OptimizelyServerSideExperiments[key] === `cro_hero_offer_image_${offerId}`
  })
}

export function getHeroFinderExperimentKey(offerId: string) {
  return Object.values(OptimizelyServerSideExperiments).find((value) => {
    return value === `cro_hero_offer_image_${offerId}`
  })
}

export type OptimizelyExperiments = OptimizelyClientSideExperiments | OptimizelyServerSideExperiments;

export enum OptimizelyClientSideExperiments {
  lereLpcLowRpvFooterEnabled = 'lere_lpc_sticky_footer_low_rpv',
  searchPriceMapMarkers = 'search_price_map_markers',
  searchTypeaheadOpenSearch = 'search_typeahead_opensearch',
  isTourReviewsEnabled = 'cro_tours_reviews',
  paymentsMerchantFees = 'payments_merchant_fees',
  paymentsFlightsMerchantFees = 'payments_flight_merchant_fees',
  signUpOfferDetails = 'cro_offer_details_sign_up_modal',
  tripExplore = 'trip_explore',
  paymentsBookingProtection = 'payments_booking_protection',
  paymentsBookingProtectionDomestic = 'payments_booking_protection_domestic',
  lereTrendingDestinations = 'lere_trending_destinations',
  lereAnywhereSearch = 'lere_anywhere_search',
  searchBedbankAnytimePrice = 'search_bedbank_anytime_price_days',
  offerPageHotelAndFlightsToggle = 'cro_offer_page_hotel_and_flights_toggle',
  searchMapSplitView = 'search_map_split_view',
  searchBroadSearchLocation = 'search_broad_search_location',
  offerPageMapInSummary = 'cro_offer_page_map_in_summary',
  offerPageNumberOfNights = 'cro_offer_page_number_of_nights',
  steppedCheckout = 'cro_stepped_checkout',
  checkoutPromoCodeUX = 'cro_checkout_promo_code_ux',
}

export enum OptimizelyServerSideExperiments {
  isSupportAssistantEnabled = 'opex_support_assistant_customer_pool',
  aaExperimentEnabled = 'aa_experiment_23_01',
  paymentsPayToRelease = 'payments_payto_release',
  newHomepageWithSignupDelay = 'cro_new_homepage_with_signup_delay',
  offerPageTruncateText = 'cro_offer_page_truncate_text',
  lereOfferPageEmbeddedCarousel = 'lere_embedded_carousel',
  searchFamilySegmentation = 'search_family_segmentation',
  loggedOutPricingEnabled = 'cro_logged_out_pricing',
  lereHomepageTopPickCarousel = 'lere_homepage_top_pick_carousel',
  strikethroughPricing = 'cro_strikethrough_price',
  heroFinderOfferMontigo = 'cro_hero_offer_image_0062y00000DNXXIAA5',
  heroFinderOfferSofitel = 'cro_hero_offer_image_0062y00000Id48mAAB',
  heroFinderOfferKappa = 'cro_hero_offer_image_0062y00000GwnFLAAZ',
  paymentsHotelsDeposits = 'payments_hotels_deposits',
  showMoreOffersOnHeroCarousel = 'cro_show_more_offers_on_hero_carousel',
  lpcChangeDatesEnabled = 'opex_lpc_change_dates',
  nonFlashNextAlgo = 'search_non_flash_next_algo',
}

export enum OptimizelyFeatureFlags {
  isGoCardlessEnabled = 'payments_go_cardless',
  isHotelOfferStickyFooterEnabled = 'cro_sticky_footer_flag',
}

export async function activateOptimizelyExperiment(flagKey: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments) {
  // This function shouldn't be called on server
  if (typeof window === 'undefined') {
    return
  }

  const success = await optimizelyIsReady

  if (success) {
    try {
      const decision = userContext.decideForKeys([flagKey])[flagKey]
      const experimentValues = {
        variationKey: decision.variationKey,
        variables: decision.variables,
        enabled: decision.enabled,
      }

      return experimentValues
    } catch (e) {
      reportClientError(e, { flagKey }) }
  }
}

/**
 * Optimizely Experiments Selectors.  Each experiment can be found in Redux in state.optimizely.optimizelyExperiments.
 * Each has an 'enabled' value that can be used to determine if the user is enrolled in a variant (if the user is
 * part of the control, or the test isn't live in Optimizely, the value will be false).  Each experiment also has
 * a 'variationKey' value that can be used to determine which variant the user is enrolled in (think of this as
 * the same thing as the number variant previously used in Optimize).  Each experiment also has a 'variables'value
 * that can be used to pass variables from Optimizely to use in the experiment.
 *
 * Return the optimizely experiment variant dynamically based on the given trigger boolean condition.
 * If canTriggerExperiment is false the experiment will not be activated.
 * Note: once the trigger becomes true once, it will be enabled for the rest of the session until page refresh.
 *
 * @param canTriggerExperiment a boolean indicating whether the experiment can be triggered
 * @param experimentId experiment id from the Optimizely UI (Rule Key)
 * @returns Experiment variant if can be triggered
 */
export function useOptimizelyExperiment(experimentId: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments, canTriggerExperiment: boolean = true) {
  const dispatch = useAppDispatch()
  const experimentActivated = useRef(false)
  useEffect(() => {
    if (canTriggerExperiment && !experimentActivated.current) {
      dispatch(setOptimizelyExperiment(experimentId))
      experimentActivated.current = true
    }
  }, [dispatch, canTriggerExperiment, experimentId])

  const experimentVariation = useAppSelector(state => getOptimizelyExperimentVariation(state, experimentId))
  // For devs to set a specific variation locally. Set REACT_APP_OPTIMIZELY_[experimentId]=true for variant or false for control in .env,
  if (config[`OPTIMIZELY_${experimentId}`] !== undefined) { return config[`OPTIMIZELY_${experimentId}`] }

  if (canTriggerExperiment) { return experimentVariation }
}

export function isClientSideExperiment(experimentId: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments): experimentId is OptimizelyClientSideExperiments {
  return Object.values(OptimizelyClientSideExperiments).includes(experimentId as OptimizelyClientSideExperiments)
}

/**
 *
 *  Gets all Optimizely experiments and returns an object required for Snowplow experiment context
 */
export function allOptimizelyExperiments() {
  const allExperiments = userContext.decideAll([OptimizelyDecideOption.DISABLE_DECISION_EVENT])
  return {
    flags: Object.entries(allExperiments).map(
      ([_, decisions]) => ({
        key: `exp_${decisions.flagKey}`,
        value: decisions.variationKey || '',
      }),
    ),
  }
}

export const getOptimizelyExperimentVariation = createSelector(
  (state: App.State, experimentId: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments) => state.optimizely.optimizelyExperiments[experimentId],
  (experiment) => {
    if (!experiment?.enabled) {
      return
    }
    if (experiment.enabled && Object.keys(experiment.variables).length) {
      return experiment.variables[experiment.variationKey]
    }
    return experiment.variationKey
  },
)

export const getOptimizelyFeatureFlagVariation = createSelector(
  (state: App.State, experimentId: OptimizelyFeatureFlags) => state.optimizely.optimizelyFeatureFlags[experimentId],
  (experiment) => {
    return !!experiment
  },
)
